import { Link } from "react-router-dom";
import { imageUrl, formatNZD, timeAgo } from "@/lib/api";
import { MapPin, Store, Share2, Navigation, Clock, Zap, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function directionsUrl(address, storeName) {
  const q = encodeURIComponent(address || storeName || "");
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

function shareLink(m) {
  return `${window.location.origin}/deal/${m.id}`;
}

function timeUntil(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3600000);
  if (h >= 24) return `${Math.floor(h/24)}d left`;
  if (h >= 1) return `${h}h left`;
  return `${Math.max(1, Math.floor(ms/60000))}m left`;
}

export default function MarkdownCard({ m }) {
  const off = m.percent_off || 0;
  const url = shareLink(m);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${m.product_name} — ${off}% OFF at ${m.store?.store_name}`, text: `In-store deal on Blurblive.nz`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — share it anywhere");
      }
    } catch (e) { /* user cancelled */ }
  };
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${m.product_name} — ${off}% OFF at ${m.store?.store_name} · ${url}`)}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

  return (
    <div className={`card-lift bg-white border rounded-2xl overflow-hidden shadow-sm ${m.boosted ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`} data-testid={`markdown-card-${m.id}`}>
      <div className="relative aspect-[4/3] bg-slate-100">
        {m.boosted && (
          <div className="absolute top-3 right-3 z-10 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full shadow flex items-center gap-1" data-testid={`boosted-${m.id}`}>
            <Zap className="w-3 h-3" />Boosted
          </div>
        )}
        {m.image_path ? (
          <img src={imageUrl(m.image_path)} alt={m.product_name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400">No image</div>
        )}
        {off > 0 && (
          <div className="absolute top-3 left-3 discount-pulse bg-red-600 text-white text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-testid={`percent-off-${m.id}`}>
            {off}% OFF
          </div>
        )}
        {m.expires_at && (
          <div className="absolute bottom-3 left-3 bg-slate-900/85 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1">
            <Clock className="w-3 h-3" />{timeUntil(m.expires_at)}
          </div>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-1" style={{display: m.boosted ? 'none' : undefined}}>
          <span className="bg-white/95 text-emerald-900 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full shadow">In-store only</span>
        </div>
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-display font-bold text-slate-900 text-base leading-tight line-clamp-2" data-testid={`product-name-${m.id}`}>{m.product_name}</h3>
        {m.description && <p className="text-sm text-slate-600 line-clamp-2">{m.description}</p>}
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-xl font-extrabold text-emerald-800" data-testid={`now-price-${m.id}`}>{formatNZD(m.now_price)}</span>
          <span className="strike text-sm" data-testid={`was-price-${m.id}`}>{formatNZD(m.was_price)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          {m.store ? (
            <Link to={`/store/${m.store.slug}`} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 hover:text-emerald-900 truncate" data-testid={`store-link-${m.id}`}>
              <Store className="w-3.5 h-3.5" />{m.store.store_name}
              {m.store.founding_seller && <Award className="w-3.5 h-3.5 text-fuchsia-700 shrink-0" title="Founding Seller" aria-label="Founding Seller" data-testid={`founding-badge-${m.id}`} />}
            </Link>
          ) : <span />}
          <span className="text-[11px] text-slate-500 flex items-center gap-1 whitespace-nowrap">
            {m.store?.region && (<><MapPin className="w-3 h-3" />{m.store.region}</>)}
          </span>
        </div>
        <div className="flex gap-1.5 pt-2">
          <a href={directionsUrl(m.store?.address, m.store?.store_name)} target="_blank" rel="noreferrer" className="flex-1" data-testid={`directions-${m.id}`}>
            <Button size="sm" className="w-full bg-emerald-800 hover:bg-emerald-900 h-8 text-xs"><Navigation className="w-3.5 h-3.5 mr-1" />Directions</Button>
          </a>
          <Button size="sm" variant="outline" onClick={share} className="h-8 text-xs px-2.5" data-testid={`share-${m.id}`}><Share2 className="w-3.5 h-3.5" /></Button>
          <a href={whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border border-slate-200 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold" data-testid={`whatsapp-${m.id}`}>WA</a>
          <a href={facebook} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border border-slate-200 text-blue-700 hover:bg-blue-50 text-xs font-semibold" data-testid={`facebook-${m.id}`}>FB</a>
        </div>
        <div className="text-[10px] text-slate-400 pt-1">{timeAgo(m.created_at)}</div>
      </div>
    </div>
  );
}
